import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  chakra,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useColorModeValue } from '@mono/chakra';
import { HeadingPortal } from '@mono/layout';
import { FC, useMemo, useState } from 'react';
import { FiClock, FiPlus, FiSettings, FiUser, FiUsers } from 'react-icons/fi';
import { useProjects } from './hooks';
import { useCurrentOrganization } from '../organizations/hooks';
import CreateProjectModal from './CreateProjectModal';
import { useNavigate } from 'react-router';

const ACCENT_GRADIENTS = [
  'linear(to-r, primary.500, primary.300)',
  'linear(to-r, purple.500, purple.300)',
  'linear(to-r, teal.500, teal.300)',
  'linear(to-r, orange.500, orange.300)',
  'linear(to-r, cyan.500, cyan.300)',
] as const;

const STATUS_VARIANTS = [
  'primary',
  'purple',
  'teal',
  'orange',
  'cyan',
] as const;

const formatAbsoluteDate = (date: Date) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);

const formatRelativeTime = (date: Date) => {
  const timestamp = date.getTime();
  if (Number.isNaN(timestamp)) {
    return { value: '—', caption: 'Awaiting activity' };
  }

  const diffMs = Date.now() - timestamp;
  if (diffMs < 60_000) {
    return {
      value: 'Just now',
      caption: `Updated ${formatAbsoluteDate(date)}`,
    };
  }
  const diffMinutes = Math.floor(diffMs / 60_000);
  if (diffMinutes < 60) {
    return {
      value: `${diffMinutes}m ago`,
      caption: `Updated ${formatAbsoluteDate(date)}`,
    };
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return {
      value: `${diffHours}h ago`,
      caption: `Updated ${formatAbsoluteDate(date)}`,
    };
  }
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) {
    return {
      value: 'Yesterday',
      caption: `Updated ${formatAbsoluteDate(date)}`,
    };
  }
  if (diffDays < 7) {
    return {
      value: `${diffDays}d ago`,
      caption: `Updated ${formatAbsoluteDate(date)}`,
    };
  }

  return {
    value: formatAbsoluteDate(date),
    caption: `Updated ${formatAbsoluteDate(date)}`,
  };
};

const formatRoleLabel = (role: string) =>
  role.charAt(0) + role.slice(1).toLowerCase();

const ProjectsListPage: FC = () => {
  const navigate = useNavigate();
  const organization = useCurrentOrganization();
  const {
    projects,
    isLoading,
    loadError,
    refresh,
    metrics,
    createProject,
    isCreating,
  } = useProjects();
  const [isCreateOpen, setCreateOpen] = useState(false);

  const pageBg = useColorModeValue('bg.subtle', 'gray.950');
  const statsCardBg = useColorModeValue('bg.surface', 'gray.850');
  const statsBorderColor = useColorModeValue('border.muted', 'whiteAlpha.200');
  const projectCardBg = useColorModeValue('bg.surface', 'gray.900');
  const projectBorderColor = useColorModeValue(
    'border.muted',
    'whiteAlpha.200'
  );

  const quickStats = useMemo(() => {
    const { totalProjects, totalCollaborators, latestUpdate } = metrics;
    const orgName = organization?.name ?? 'this workspace';
    const latestMeta = latestUpdate ? formatRelativeTime(latestUpdate) : null;

    return [
      {
        id: 'projects',
        label: 'Active projects',
        value: totalProjects.toString(),
        caption: totalProjects
          ? `You’re collaborating on ${totalProjects} project${
              totalProjects === 1 ? '' : 's'
            } in ${orgName}.`
          : 'Create your first project to kick things off.',
      },
      {
        id: 'collaborators',
        label: 'Collaborators involved',
        value: totalCollaborators.toString(),
        caption: totalProjects
          ? `Average of ${
              totalProjects > 0
                ? Math.max(1, Math.round(totalCollaborators / totalProjects))
                : 0
            } teammates per project.`
          : 'Invite teammates once a project is created.',
      },
      {
        id: 'recent',
        label: 'Most recent update',
        value: latestMeta?.value ?? '—',
        caption: latestMeta?.caption ?? 'No project activity yet.',
      },
    ];
  }, [metrics, organization?.name]);

  const decoratedProjects = useMemo(
    () =>
      projects.map((project, index) => {
        const accent = ACCENT_GRADIENTS[index % ACCENT_GRADIENTS.length];
        const statusPalette = STATUS_VARIANTS[index % STATUS_VARIANTS.length];
        const updateMeta = formatRelativeTime(new Date(project.updatedAt));
        const roleLabel = formatRoleLabel(project.role ?? 'READ');

        return {
          ...project,
          accent,
          statusPalette,
          status: 'Active',
          updateMeta,
          roleLabel,
        };
      }),
    [projects]
  );

  const showEmptyState =
    !isLoading && !loadError && decoratedProjects.length === 0;

  const handleProjectClick = (projectId: string) => {
    navigate(`/${organization?.slug}/p/${projectId}`);
  };

  const handleProjectSettingsClick = (projectId: string) => {
    navigate(`/${organization?.slug}/p/${projectId}/settings`);
  };

  return (
    <Stack
      gap="10"
      px={{ base: 4, md: 10 }}
      py={{ base: 6, md: 10 }}
      bg={pageBg}
      minH="full"
    >
      <HeadingPortal>Projects</HeadingPortal>

      <SimpleGrid columns={{ base: 1, md: 3 }} gap="6">
        {quickStats.map((stat) => (
          <Card.Root
            key={stat.id}
            variant="outline"
            bg={statsCardBg}
            borderColor={statsBorderColor}
          >
            <Card.Body gap="3" p={{ base: 5, md: 6 }}>
              <Text fontSize="sm" color="fg.muted">
                {stat.label}
              </Text>
              {isLoading ? (
                <Skeleton height="8" width="50%" />
              ) : (
                <>
                  <chakra.p fontSize="2xl" fontWeight="semibold">
                    {stat.value}
                  </chakra.p>
                  <Text fontSize="sm" color="fg.subtle">
                    {stat.caption}
                  </Text>
                </>
              )}
            </Card.Body>
          </Card.Root>
        ))}
      </SimpleGrid>

      {loadError ? (
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>We couldn’t load your projects</Alert.Title>
            <Alert.Description>{loadError}</Alert.Description>
            <Button
              onClick={() => void refresh()}
              alignSelf="flex-start"
              mt="3"
              variant="outline"
            >
              Try again
            </Button>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      <HStack justify="space-between" align="center" gap="4">
        <Stack gap="1" align="flex-start">
          <chakra.h2 fontSize="lg" fontWeight="semibold">
            Your projects
          </chakra.h2>
          <Text fontSize="sm" color="fg.muted">
            A curated view of the workstreams you collaborate on.
          </Text>
        </Stack>
        <Button
          gap="2"
          colorPalette="primary"
          size="lg"
          onClick={() => setCreateOpen(true)}
          loading={isCreating && isCreateOpen}
        >
          <Icon as={FiPlus} />
          New Project
        </Button>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="6">
        {isLoading
          ? Array.from({ length: 3 }).map((_, index) => (
              <Card.Root key={`skeleton-${index}`} height="full">
                <Skeleton height="200px" />
              </Card.Root>
            ))
          : null}

        {!isLoading &&
          decoratedProjects.map((project) => (
            <Card.Root
              key={project.id}
              variant="elevated"
              height="full"
              bg={projectCardBg}
              display="flex"
              overflow="hidden"
              borderColor={projectBorderColor}
              onClick={() => handleProjectClick(project.id)}
              cursor={'pointer'}
            >
              <Box h="1" bgGradient={project.accent} />
              <Card.Body display="flex" flexDirection="column" gap="3">
                <HStack justify="space-between" align="flex-start" gap="3">
                  <Stack gap="2">
                    <chakra.h3 fontSize="xl" fontWeight="semibold">
                      {project.name}
                    </chakra.h3>
                    <Text color="fg.muted">
                      {project.description || 'No description provided yet.'}
                    </Text>
                  </Stack>
                  <Badge
                    variant="solid"
                    colorPalette={project.statusPalette}
                    borderRadius="full"
                  >
                    {project.status}
                  </Badge>
                </HStack>
              </Card.Body>
              <Card.Footer
                borderTopWidth="1px"
                borderColor={projectBorderColor}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap="4"
                py="4"
                px="6"
              >
                <HStack gap="3" color="fg.subtle">
                  <HStack gap="2">
                    <Icon as={FiUsers} />
                    <Text fontSize="sm">
                      {project.collaboratorsCount} collaborators
                    </Text>
                  </HStack>
                  <chakra.span>•</chakra.span>
                  <HStack gap="2">
                    <Icon as={FiClock} />
                    <Text fontSize="sm" title={project.updateMeta.caption}>
                      {project.updateMeta.value}
                    </Text>
                  </HStack>
                </HStack>
                <Badge
                  variant="subtle"
                  colorPalette="primary"
                  display="flex"
                  alignItems="center"
                  gap="1"
                  px="3"
                  py="1"
                  borderRadius="full"
                  marginLeft={'auto'}
                >
                  <Icon as={FiUser} />
                  <Text fontSize="sm">{project.roleLabel}</Text>
                </Badge>
                <IconButton
                  size={'sm'}
                  variant={'plain'}
                  borderRadius={50}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProjectSettingsClick(project.id);
                  }}
                >
                  <Icon as={FiSettings} />
                </IconButton>
              </Card.Footer>
            </Card.Root>
          ))}

        {showEmptyState ? (
          <Card.Root
            gridColumn={{ base: 'span 1', md: 'span 2', xl: 'span 3' }}
            variant="outline"
            borderStyle="dashed"
            borderColor="border.subtle"
          >
            <Card.Body
              gap="4"
              alignItems="center"
              textAlign="center"
              py={{ base: 8, md: 12 }}
            >
              <chakra.h3 fontSize="xl" fontWeight="semibold">
                You haven’t created any projects yet
              </chakra.h3>
              <Text color="fg.muted" maxW="lg">
                Spin up your first project to start planning work, assembling
                collaborators, and tracking progress in one place.
              </Text>
              <Button
                gap="2"
                colorPalette="primary"
                size="lg"
                onClick={() => setCreateOpen(true)}
                loading={isCreating && isCreateOpen}
              >
                <Icon as={FiPlus} />
                Create a project
              </Button>
            </Card.Body>
          </Card.Root>
        ) : null}
      </SimpleGrid>

      <CreateProjectModal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (values) => {
          await createProject(values);
        }}
      />
    </Stack>
  );
};

export default ProjectsListPage;
